/* eslint-disable camelcase */
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SaveAsBlock } from 'components/SaveAsSection';
import { resetFilters } from 'components/ThreeJs/assets/main';
import { AppDataContext } from 'context/appData';
import { ConfigurationDataContext, GlassStructureContext } from 'context/configuration';
import { UiContext } from 'context/ui';
import GlassProductSideOption from './GlassProductSideOption';
import InfoBox from './InfoBox';

const sqmPriceFormat = price => {
	if (!price) {
		return price;
	}
	const roundedPrice = Math.round(price);
	if (roundedPrice === price) {
		return `${price}.00`;
	}
	return `${Math.round(price * 100) / 100.0}`; // Display 2 digits
};

const sqmPricesFormat = prices =>
	prices
		? Object.fromEntries(Object.entries(prices).map(([vol, p]) => [vol, sqmPriceFormat(p)]))
		: prices;

const GlassProductSidebar = ({ name, rightKeys }) => {
	const { t } = useTranslation('commonConfigurationAction');
	const { configuratorData, isConfiguratorLoaded } = useContext(AppDataContext);
	const { addNewConfiguration, editingGlassIdRef, saveEditedConfiguration, isLoading } =
		useContext(ConfigurationDataContext);
	const { flatGlassStructure, glassConfig, uiHandlerRef, takeScreenshot, showPrice } =
		useContext(GlassStructureContext);
	const { showPopupByKey } = useContext(UiContext);
	const [expectingGlassConfig, setExpectingGlassConfig] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [glassSqmData, setGlassSqmData] = useState(null);

	const { min_pos_sqm, model_glass_factor } = configuratorData?.pricing || {
		min_pos_sqm: parseFloat('nan'),
		model_glass_factor: parseFloat('nan'),
	};

	const onAddPositionHandler = () => {
		// changes glassConfig when done which is picked up by useEffect
		setExpectingGlassConfig(true);
		takeScreenshot();
		setSubmitting(true);
	};

	const showResetGlassPopup = e => {
		e.stopPropagation();
		showPopupByKey('resetGlass');
	};

	const showResetConfigurationPopup = e => {
		e.stopPropagation();
		showPopupByKey('resetConfiguration');
	};

	useEffect(() => {
		if (expectingGlassConfig && Object.keys(glassConfig).length > 0 && !isLoading) {
			if (editingGlassIdRef.current === null) {
				addNewConfiguration(glassConfig);
			} else {
				saveEditedConfiguration(glassConfig);
			}
			setExpectingGlassConfig(false);
			resetFilters(); // reset the glass on addPosition
			if (submitting) {
				setSubmitting(false);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [glassConfig, expectingGlassConfig, isLoading]);

	useEffect(() => {
		if (flatGlassStructure) {
			const sqmData = {
				prices: sqmPricesFormat(flatGlassStructure.sqmPrices),
				weight: flatGlassStructure.sqmWeight,
			};
			setGlassSqmData({ price: sqmData.prices?.['0'], ...sqmData });
		}
	}, [flatGlassStructure]);

	if (!uiHandlerRef.current?.isDbLoaded) {
		// Don't render GlassProductSideOptions which contains all AbstractUI elements.
		// They depend on the DB being loaded to initialize
		return <div id={name} className="glass_product_col info_mod" />;
	}
	const hasPrice = glassSqmData && glassSqmData.price !== null;
	const blurPrice = !(showPrice || (!showPrice && !hasPrice));
	const priceText = glassSqmData?.price || t('priceOnDemand');
	return (
		<div id={name} className="glass_product_col info_mod">
			<div className="instruction instruction_side">
				2.&nbsp;
				{t('secondCustomize')}
			</div>
			{rightKeys.map(f => (
				<GlassProductSideOption key={f} name={f} uiHandlerRef={uiHandlerRef} />
			))}
			<div className="glass_product_col_ctrl">
				<div className="glass_product_sqm_data glass_product_selection_info">
					<div className="glass_product_sqm_weight">
						{glassSqmData?.weight || '--'}
						&nbsp;kg/m
						<sup>2</sup>
					</div>
					<div>
						<span className="glass_product_sqm_price">
							<span className={blurPrice ? 'glass_product_sqm_price_blur' : ''}>
								{((showPrice || (!showPrice && !hasPrice)) && priceText) || t('blurredPrice')}
								&nbsp;
							</span>
							{hasPrice && (
								<>
									CHF/m
									<sup>2&nbsp;</sup>
								</>
							)}
							<sup>
								<InfoBox
									hoverMessage={
										showPrice
											? hasPrice
												? t('priceInfoBoxFmt')
														.replace('{min_pos_sqm}', min_pos_sqm)
														.replace(
															'{model_glass_factor}',
															Math.round((model_glass_factor - 1.0) * 10000) / 100
														)
												: t('priceNotAvailableInfoBox')
											: t('loginForPriceInfoBox')
									}
								/>
							</sup>
						</span>
						{showPrice && hasPrice && (
							<>
								<br />
								<div className="glass_product_sqm_prices_list">
									<div className="glass_product_price_table_header_row">
										<div className="glass_product_price_table_volume">{t('orderVolume')}</div>
										<div className="glass_product_price_table_sqm_price">{t('chfPerSqm')}</div>
									</div>
									{Object.keys(glassSqmData.prices)
										.filter((v, i) => v !== '0' && i)
										.map(p => (
											<div className="glass_product_price_table_row" key={p}>
												<div className="glass_product_price_table_volume">
													{t('volumeFromPrice').replace('{}', p)}
												</div>
												<div className="glass_product_price_table_sqm_price">
													{glassSqmData.prices[p]}
												</div>
											</div>
										))}
								</div>
							</>
						)}
					</div>
				</div>
				<div className="btn_wrap offset_mod flex_mod">
					<div className="cart_pdf">
						<button
							disabled={!isConfiguratorLoaded || submitting}
							className="btn_v3"
							type="button"
							onClick={onAddPositionHandler}
						>
							<span className="btn_in">
								{editingGlassIdRef.current === null ? t('recordPositions') : t('updateGlassType')}
							</span>
						</button>
						<SaveAsBlock
							pdfData={glassConfig}
							takeScreenshot={takeScreenshot}
							isDisabled={!isConfiguratorLoaded}
							isHomePage
						/>
					</div>
				</div>
				<ul className="save_as_block__list mod_left_align">
					<li className="save_as_block__list_item">
						<button
							className="btn_v4 save_as_block__glass_button"
							type="button"
							onClick={showResetGlassPopup}
						>
							{t('resetGlass')}
						</button>
					</li>
					<li className="save_as_block__list_item">
						<button
							className="btn_v4 save_as_block__glass_button"
							type="button"
							onClick={showResetConfigurationPopup}
						>
							{t('resetConfiguration')}
						</button>
					</li>
				</ul>
			</div>
		</div>
	);
};

export default GlassProductSidebar;
